import {
    ICacheFactoryService,
    IHttpService,
    IPromise,
    IQService,
} from 'angular';
import { StateParams, StateService, UIRouter } from 'angular-ui-router';
import { ngStorage } from 'ngstorage';
import {
    ErrorResponse,
    ExtraSignoutRequestArgs,
    SigninRedirectArgs,
    User,
    UserManager,
} from 'oidc-client-ts';
import { BehaviorSubject, Subject } from 'rxjs';
import { standardizeUrl, TnError, UserModel } from '../tn.common';
import DjangoClient from '../tn.django/django-client.service/djangoClient';

export class UserService {
    // emits on signup, login, logout, and save
    public user$: BehaviorSubject<UserModel | undefined>;
    // this is the user returned by OIDC it is a a mix of credentials and a profile that is a subset of the user model.
    // we populate it as null by default, until we get a user loaded event.
    public oidcUser$ = new BehaviorSubject<User | null>(null);

    // emits on signup, login, and hijack login events
    public login$ = new Subject<UserModel>();
    // emits on all logout and hijack logout events
    public logout$ = new Subject();
    // emits on all signup events
    public signup$ = new Subject<UserModel>();

    // OIDC specific subjects. These can be used where we need less then the full User model.
    public accessToken$ = new BehaviorSubject<string>('');
    public error$ = new BehaviorSubject<unknown>(undefined);
    public isAuthenticated$ = new BehaviorSubject<boolean>(false);

    public ready: Promise<User | null | void>;

    protected userManager: UserManager;

    // current user.
    protected user?: UserModel;
    // post logout redirect uri
    protected postLogoutRedirecUri: string;

    // during a hijacked session, this holds the original user.
    protected hijacker?: UserModel;
    protected hijackee?: UserModel;

    static $inject = [
        '$cacheFactory',
        '$http',
        '$localStorage',
        '$q',
        '$state',
        '$uiRouter',
        'apiBaseURl',
        'djangoClient',
        'tnAuthIssuer',
        'tnAuthClientId',
        'tnAuthRedirectUri',
        'tnAuthPostLogoutRedirectUri',
        'tnAuthExtraOptions',
    ];
    constructor(
        protected $cacheFactory: ICacheFactoryService,
        protected $http: IHttpService,
        protected $localStorage: ngStorage.StorageService,
        protected $q: IQService,
        protected $state: StateService,
        protected $uiRouter: UIRouter,
        protected apiBaseURl: string,
        protected djangoClient: DjangoClient,
        protected issuer: string,
        protected clientId: string,
        protected redirectUri: string,
        postLogoutRedirecUri: string,
        protected extraOptions = {}
    ) {
        // console.debug('UserService.constructor');
        // use the deployment url as the default base for the post logout redirect uri.
        // this supports local and ci development.
        this.postLogoutRedirecUri = standardizeUrl(
            window.location.origin,
            postLogoutRedirecUri
        );
        const oidcSettings = {
            authority: issuer,
            client_id: clientId,
            redirect_uri: redirectUri,
            post_logout_redirect_uri: this.postLogoutRedirecUri,
            response_type: 'code',
            scope: 'openid profile email',
            automaticSilentRenew: true,
            ...extraOptions,
        };

        this.userManager = new UserManager(oidcSettings);

        this.userManager.events.addUserLoaded((oidcUser) => {
            // console.debug(
            //     'UserService.userManager.events.addUserLoaded',
            //     oidcUser
            // );
            const accessToken = `${oidcUser.token_type} ${oidcUser.access_token}`;
            this.accessToken$.next(accessToken);
            this.oidcUser$.next(oidcUser);
            this.isAuthenticated$.next(true);

            // #region legacy compatibility.
            const userId = parseInt(oidcUser.profile.sub);

            // if we didn't get a valid user id, log an error and return.
            if (isNaN(userId)) {
                console.error(
                    'UserService.userManager.events.addUserLoaded: invalid user id',
                    { oidcUser, userId }
                );
                this.userManager.removeUser();
                return;
            }

            // fetch the user if we got a valid user id.
            this.fetchUser(userId)
                .then((user) => {
                    this.user = user;
                    this.login$.next(this.user);
                    this.user$.next(this.user);
                })
                .catch((error) => {
                    // TODO: should we logout here?  in this state this.user would be incorrect.
                    console.error(
                        'UserService.userManager.events.addUserLoaded: could not fetch user',
                        error
                    );
                    this.error$.next(error);
                });
            // #endregion
        });

        this.userManager.events.addUserUnloaded(() => {
            // console.debug('UserService.userManager.events.addUserUnloaded');
            this.accessToken$.next('');
            this.oidcUser$.next(null);
            this.isAuthenticated$.next(false);
            this.user$.next(undefined);
            this.logout$.next(undefined);
        });

        this.userManager.events.addSilentRenewError((e) => {
            console.error(
                'UserService.userManager.events.addSilentRenewError',
                e
            );
            this.error$.next(`SilentRenewError: ${e.message}`);
        });

        this.accessToken$.subscribe((token) => {
            this.djangoClient.accessToken = token;
        });

        this.user$ = new BehaviorSubject(this.user);

        // start with a silent signin to load the user if they're already authenticated.
        // console.debug('UserService.constructor.ready::silent signin', { $state });
        this.ready = this.userManager.signinSilent().catch((error) => {
            if (error instanceof ErrorResponse) {
                if (error.error === 'login_required') {
                    // ignore login_required errors for ready...
                    return;
                }
            }
            // log any other errors, but still swallow, this is only a ready check and shouldn't raise errors.
            console.error(
                'UserService.constructor.ready::silent signin error',
                error,
                JSON.stringify(error)
            );
            throw error;
        });

        // this.user$.subscribe((user) => {
        //     console.debug('UserService.user$', user);
        // });
        // this.oidcUser$.subscribe((oidcUser) => {
        //     console.debug('UserService.oidcUser$', oidcUser);
        // });
        // this.login$.subscribe((user) => {
        //     console.debug('UserService.login$', user);
        // });
        // this.logout$.subscribe(() => {
        //     console.debug('UserService.logout$');
        // });
        // this.signup$.subscribe((user) => {
        //     console.debug('UserService.signup$', user);
        // });
        // this.accessToken$.subscribe((token) => {
        //     console.debug('UserService.accessToken$', token);
        // });
        // this.error$.subscribe((error) => {
        //     console.debug('UserService.error$', error);
        // });
        // this.isAuthenticated$.subscribe((isAuthenticated) => {
        //     console.debug('UserService.isAuthenticated$', isAuthenticated);
        // });
    }

    public async oauthLogin(
        stateName = this.$uiRouter.globals.current.name,
        stateParams = this.$uiRouter.globals.params
    ): Promise<void> {
        const state = {
            stateName,
            stateParams,
        };

        const siginRedirectArgs: SigninRedirectArgs = {
            state,
        };

        this.userManager.signinRedirect(siginRedirectArgs);
    }

    public oauthLogout(
        stateName?: string,
        stateParams?: StateParams
    ): IPromise<void> {
        // if a state is not provided, determine the default state based on the current state.
        if (!stateName) {
            if (this.$uiRouter.globals.current.data?.requireAuthorized) {
                stateName = 'home';
                stateParams = undefined;
            } else {
                stateName = this.$uiRouter.globals.current.name;
                stateParams = this.$uiRouter.globals.params;
            }
        }

        const state = {
            stateName,
            stateParams,
        };

        const args: ExtraSignoutRequestArgs = { state };
        return this.userManager.signoutRedirect(args);
    }

    public oauthRefresh(): IPromise<boolean> {
        // console.debug('UserService.oauthRefresh');
        return this.userManager
            .signinSilent()
            .then(() => {
                return true;
            })
            .catch((e) => {
                // set error state for reactive handling
                this.error$.next(e);
                return false;
            });
    }

    public handleOAuthRedirect($stateParams: StateParams): void {
        // console.debug('handleOAuthRedirect', $stateParams);

        if ($stateParams.error) {
            // if the error is not login_required, then we have a real error.
            // login_require is set during a silentLogin/renew when there is no longer a session at the IDP.
            if ($stateParams.error !== 'login_required') {
                const message = $stateParams.error_description;
                const error = new Error(message);
                this.error$.next(error);
            }
        }

        this.userManager
            .signinCallback()
            .then((response) => {
                // state passed through OAuth redirect.
                const state = response?.state;
                if (state) {
                    const stateName = state['stateName'];
                    if (stateName) {
                        const stateParams = state['stateParams'] || {};
                        // defer state.go one tick to allow auth handlers to complete.
                        setTimeout(() => {
                            this.$state.go(stateName, stateParams, {
                                location: 'replace',
                            });
                        }, 0);
                        return;
                    }
                }
                this._defaultPostRedirectTransition();
                return;
            })
            .catch((error) => {
                this.error$.next(error);
                console.error(
                    'UserService.handleOAuthRedirect:signinCallback error',
                    error
                );
                this._defaultPostRedirectTransition();
            });
    }

    protected _defaultPostRedirectTransition(): void {
        this.$state.go('home.fonts.listing', {}, { location: 'replace' });
    }

    public handleOAuthSignoutRedirect($stateParams: StateParams): void {
        if (!$stateParams.state) {
            console.debug(
                'UserService.handleOAuthSignoutRedirect::no state in $stateParams',
                { $stateParams }
            );
            this._defaultPostRedirectTransition();
            return;
        }

        console.debug(
            'UserService.handleOAuthSignoutRedirect::oidc.signoutCallback::handle state'
        );
        this.userManager
            .signoutRedirectCallback()
            .then((response) => {
                console.debug(
                    'UserService.handleOAuthSignoutRedirect::oidc.signoutCallback::response',
                    response
                );
                // state passed through OAuth redirect.
                const userState = response?.userState;
                if (userState) {
                    // goto the home page if there was no state with another state name.
                    const stateName = userState['stateName'];
                    if (stateName) {
                        const stateParams = userState['stateParams'] || {};
                        console.debug(
                            'UserService.handleOAuthSignoutRedirect::oidc.signoutCallback::redirecting',
                            { stateName, stateParams }
                        );
                        this.$state.go(stateName, stateParams, {
                            location: 'replace',
                        });
                        return;
                    }
                }
                console.debug(
                    'UserService.handleOAuthSignoutRedirect::oidc.signoutCallback::no saved userState'
                );
                this._defaultPostRedirectTransition();
                return;
            })
            .catch((error) => {
                this.error$.next(error);
                console.error(
                    'UserService.handleOAuthSignoutRedirect::oidc.signoutCallback::error',
                    error
                );
                this._defaultPostRedirectTransition();
            });
    }

    public getUser(): UserModel | undefined {
        return this.user$.value;
    }

    public isLoggedIn(): boolean {
        return !!this.oidcUser$.value;
    }

    public resetPassword(email: string): IPromise<unknown> {
        return this.$http.post(this.apiBaseURl + '/api/1/users/reset/', {
            username: email,
        });
    }

    public changeResetPassword(
        uid: string,
        token: string,
        password: string
    ): IPromise<unknown> {
        return this.$http.post(
            this.apiBaseURl + '/api/1/users/' + uid + '/change_reset_password/',
            {
                password: password,
                token: token,
            }
        );
    }

    clearHttpCache(): void {
        this.$cacheFactory.get('$http').removeAll();
        this.$cacheFactory.get('$httpRequestTime').removeAll();
    }

    ishijacked(): boolean {
        const oidcUser = this.oidcUser$.value;
        const user = this.user$.value;
        return oidcUser?.profile.sid != user?.id?.toString();
    }

    hijackLogin(uid: string): IPromise<UserModel> {
        console.log('UserService.hijackLogin', uid);
        // always release a hijack and return to the original user before
        // starting a hijack session. We don't want to deal with the
        // complexity of a tree of hijackings.
        if (this.ishijacked() || this.user$.value?.hijacked) {
            this.hijackLogout();
        }
        const url = `/api/1/users/${uid}/hijack/`;
        return this.djangoClient
            .post<{ user: UserModel; token: string }>(url)
            .then((result) => {
                if (!result) {
                    throw new TnError('hijack error, no user loaded', result);
                }
                this.hijacker = this.user$.value;
                const access_token = `Token ${result.token}`;
                const user = result.user;
                user.hijacked = true;
                this.user$.next(user);
                this.accessToken$.next(access_token);
                this.login$.next(user);
                this.user$.next(user);
                return user;
            })
            .catch((error) => {
                console.error('hijack Error', error);
                throw new TnError('hijack error', error);
            });
    }

    hijackLogout(): IPromise<void> {
        const oidcUser = this.oidcUser$.value;
        const user = this.user$.value;
        if (!oidcUser) {
            return this.userManager.removeUser();
        }

        const userId = parseInt(oidcUser.profile.sub);
        if (isNaN(userId)) {
            console.error(
                'UserService.hijackLogout error, invalid oidc userId',
                oidcUser
            );
            return this.userManager.removeUser();
        }
        this.clearHttpCache();

        this.logout$.next(this.user);

        this.accessToken$.next(`Bearer ${oidcUser.access_token}`);
        return this.fetchUser(userId)
            .then((hijacker) => {
                if (user) {
                    this.logout$.next(user);
                }
                this.user$.next(hijacker);
                this.login$.next(hijacker);
            })
            .catch((error) => {
                console.error(
                    'UserService.hijackLogout error unable to fetch hijacker UserModel',
                    error
                );
            });
    }

    releaseHijacked(): void {
        this.hijackLogout().then(() => {
            // is this the right way to navigate?
            this.$state.go('home');
        });
    }

    // TODO: move to a UserRepository
    fetchUser(id: number): IPromise<UserModel> {
        // we user access token here, since we havent' setup the new user yet.
        const url = `/api/1/users/${id}`;
        return this.djangoClient.get<UserModel>(url);
    }

    // TODO: move to a UserRepository
    saveUser(user: UserModel): IPromise<UserModel> {
        const url = `/api/1/users/${user.id}`;
        return this.djangoClient
            .put<UserModel>(url, user)
            .then((updated) => {
                this.user$.next(updated);
                return updated;
            })
            .catch((error) => {
                console.error('UserService.saveUser error', error);
                throw error;
            });
    }
}
